import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IonContent, Platform } from '@ionic/angular';
import { HeroInterface } from 'src/app/components/dynamic-html/nodes/hero/hero.component';
import { ApiService } from 'src/app/services/api.service';
import { JsonLdService } from 'src/app/services/json-ld.service';

import { SeoService } from 'src/app/services/seo.service';
import { PostItem } from 'src/app/types/post';
import { SEOSchema } from 'src/app/types/seo';
import { removeTags } from 'src/app/utils/string-transform';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-page-detail',
  templateUrl: './page-detail.page.html',
  styleUrls: ['./page-detail.page.scss'],
})
export class PageDetailPage implements OnInit {
  @ViewChild(IonContent) content: IonContent;

  cordova: boolean = false;
  showToolbar: boolean = false;
  data: PostItem;
  hero: HeroInterface;

  schema: SEOSchema = {
    '@context': 'http://schema.org',
    '@type': 'WebPage',
    author: {
      '@type': 'Organization',
      name: environment.environment_title + ' Shopping Centre',
    },
    publisher: {
      '@type': 'Organization',
      name: environment.environment_title,
    },
  };

  constructor(
    private platform: Platform,
    private route: ActivatedRoute,
    private API: ApiService,
    private seo: SeoService,
    private datePipe: DatePipe,
    private jsonLdService: JsonLdService
  ) {
    this.platform.ready().then(() => {
      this.cordova = this.platform.is('cordova');
    });
  }

  /**
   * On scroll toggle header background
   */
  onScroll($event: CustomEvent) {
    if ($event && $event.detail && $event.detail.scrollTop) {
      const scrollTop = $event.detail.scrollTop;
      this.showToolbar = scrollTop >= 160;
    }
  }

  /**
   * Set SEO & Schema data
   */
  setHeadData() {
    // Set Meta & Social tags
    if (!this.cordova) {
      let schemaData = { ...this.schema };

      if (this.data?.title && this.data?.title.toLowerCase().includes('faqs')) {
        schemaData = {
          ...schemaData,
          '@type': 'FAQPage',
        };
      }

      if (this.data?.featuredImage) {
        schemaData = {
          ...schemaData,
          image: this.data.featuredImage,
        };
      }

      if (this.data.updatedAt) {
        schemaData = {
          ...schemaData,
          dateModified: this.data.updatedAt,
        };
      }

      // Title tag
      if (this.data.seo?.title) {
        this.seo.setTitle(this.data.seo.title);
        schemaData = {
          ...schemaData,
          headline: this.data.seo.title,
        };
      } else {
        this.seo.setTitle(this.data.title);
        schemaData = {
          ...schemaData,
          headline: this.data.title,
        };
      }

      // Meta tags
      if (this.data.seo) {
        this.seo.setMeta(this.data.seo);
        this.seo.setRobotsMeta(this.data.seo);

        schemaData = {
          ...schemaData,
          description: this.data.seo.description,
        };
      }

      // Social tags
      if (this.data.social) {
        if (!this.data.social.socialImage && this.data.socialImage)
          this.data.social.socialImage = this.data.socialImage;

        this.seo.setSEOMeta(this.data.social);
      }

      // Author
      if (this.data.createdBy) {
        this.seo.setAuthor(this.data.createdBy);
      }

      // Canonical URL
      if (this.data.seo?.canonical_url) {
        this.seo.setCanonicalURL(this.data.seo);
      }

      this.schema = { ...this.schema, ...schemaData };
      this.jsonLdService.insertSchema(this.schema);

      if (this.data?.body?.blocks) {
        const faqSection = this.data?.body?.blocks.filter(
          (block) => block.type === 'faqs'
        );

        if (faqSection[0] && faqSection[0].data?.faqs) {
          this.API.GET(
            `public/faqs/${environment.mall_id}/${faqSection[0].data?.faqs}`
          ).subscribe({
            next: (response) => {
              let faqItems = [];

              response.forEach((faq, index) => {
                faqItems.push({
                  '@type': 'Question',
                  name: faq.title,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: removeTags(faq.answer),
                  },
                });
              });

              this.schema = { ...this.schema, mainEntity: faqItems };
              this.jsonLdService.insertSchema(this.schema);
            },
            error: (response) => {},
          });
        }
      }
    }
  }

  ngOnInit() {
    this.route.data.subscribe((response: { data: PostItem }) => {
      const body = response.data.body?.blocks.filter((block) => {
        if (block.type === 'hero') {
          this.hero = block.data;
          return false;
        }

        return true;
      });

      this.data = {
        ...response.data,
        body: { blocks: body },
      };

      // Set Meta & Social tags
      if (!this.cordova) {
        this.setHeadData();
      }
    });
  }

  ionViewWillEnter() {
    this.content.scrollToTop();
    this.showToolbar = false;
  }
}
